import { useCallback, useEffect, useRef, useState } from 'react';
import { EMAIL_JOURNEYS, type EmailTemplateVersion, type EmailTemplateView } from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { emailState, sentWhen, unsent } from '../lib/emails.js';
import { useConfirm } from '../lib/useConfirm.js';
import { Icon } from './Icon.js';
import { Switch } from './Choices.js';
import { useToast } from './Toast.js';

/**
 * Settings → Applicant emails: grouped by journey (F10 P5), each editable as
 * plain text with a live branded preview, a test send, on/off, the built-in
 * wording and version history. The API does the rendering and all the checks.
 */

const ACCOUNT_DETAILS = '[[online account details]]';
const ukDateTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const fmt = (iso: string) => ukDateTime.format(new Date(iso)).replace(',', '');

interface Draft {
  subject: string;
  source: string;
  enabled: boolean;
}
const draftOf = (t: EmailTemplateView): Draft => ({ subject: t.subject, source: t.source, enabled: t.enabled });
const same = (a: Draft, b: Draft) => a.subject === b.subject && a.source === b.source && a.enabled === b.enabled;

export function EmailTemplates() {
  const toast = useToast();
  const [templates, setTemplates] = useState<EmailTemplateView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDialog, ask] = useConfirm();

  useEffect(() => {
    api
      .listEmailTemplates()
      .then((r) => {
        setTemplates(r.templates);
        const first = r.templates[0];
        if (first) {
          setSelected(first.key);
          setDraft(draftOf(first));
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Couldn’t load the applicant emails'));
  }, []);

  const current = templates?.find((t) => t.key === selected) ?? null;
  const dirty = !!current && !!draft && !same(draft, draftOf(current));

  const choose = async (t: EmailTemplateView) => {
    if (t.key === selected) return;
    if (
      dirty &&
      !(await ask({
        title: 'Discard your changes?',
        body: `Your changes to “${current?.name}” haven’t been saved.`,
        confirmLabel: 'Discard',
        danger: true,
      }))
    )
      return;
    setSelected(t.key);
    setDraft(draftOf(t));
  };

  const replace = (t: EmailTemplateView) => {
    setTemplates((prev) => prev?.map((x) => (x.key === t.key ? t : x)) ?? null);
    setDraft(draftOf(t));
  };

  if (error) return <div className="card" style={{ padding: 16, color: 'var(--st-declined)' }}>{error}</div>;
  if (!templates || !current || !draft) return <div style={{ color: 'var(--text-3)' }}>Loading…</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 240px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
      {confirmDialog}
      <div className="card" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }} role="listbox" aria-label="Applicant emails">
        {EMAIL_JOURNEYS.map((j) => {
          const items = j.keys.map((k) => templates.find((t) => t.key === k)).filter((t): t is EmailTemplateView => Boolean(t));
          if (!items.length) return null;
          return (
            <div key={j.label} role="group" aria-label={j.label}>
              <div
                style={{
                  padding: '10px 10px 4px',
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--jfh-cactus)',
                }}
              >
                {j.label}
              </div>
              {items.map((t) => {
                const on = t.key === selected;
                const st = emailState(t);
                return (
                  <button
                    key={t.key}
                    role="option"
                    aria-selected={on}
                    onClick={() => void choose(t)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      borderRadius: 7,
                      padding: '8px 10px',
                      cursor: 'pointer',
                      background: on ? 'var(--accent-soft)' : 'transparent',
                      color: on ? 'var(--accent-text)' : 'var(--text-1)',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{t.name}</div>
                    <div style={{ fontSize: 11.5, marginTop: 2, fontWeight: st.bad ? 700 : 500, color: st.bad ? 'var(--st-declined)' : 'var(--text-3)' }}>
                      {st.label}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <Editor
        key={current.key}
        template={current}
        draft={draft}
        setDraft={setDraft}
        dirty={dirty}
        onSaved={(t, msg) => {
          replace(t);
          toast({ title: msg, body: t.name, icon: 'check' });
        }}
      />
    </div>
  );
}

function Editor({
  template,
  draft,
  setDraft,
  dirty,
  onSaved,
}: {
  template: EmailTemplateView;
  draft: Draft;
  setDraft: (d: Draft) => void;
  dirty: boolean;
  onSaved: (t: EmailTemplateView, message: string) => void;
}) {
  const toast = useToast();
  const area = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'test' | 'reset' | null>(null);
  const [history, setHistory] = useState<EmailTemplateVersion[] | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [confirmDialog, ask] = useConfirm();
  const notSent = unsent(template.key);

  // Live preview of the unsaved draft (debounced); the API also validates it.
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api
        .previewEmailTemplate(template.key, { subject: draft.subject, source: draft.source })
        .then((r) => {
          if (!live) return;
          setPreview(r);
          setProblem(null);
        })
        .catch((e: unknown) => live && setProblem(e instanceof Error ? e.message : 'Preview failed'));
    }, 350);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [template.key, draft.subject, draft.source]);

  const loadHistory = useCallback(() => {
    api
      .emailTemplateVersions(template.key)
      .then((r) => setHistory(r.versions))
      .catch(() => setHistory([]));
  }, [template.key]);

  useEffect(() => {
    if (showHistory) loadHistory();
  }, [showHistory, loadHistory]);

  /** Wrap the selection (or insert at the cursor) in the message box. */
  const edit = (fn: (sel: string) => { text: string; select?: [number, number] }) => {
    const el = area.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b, value } = el;
    const { text, select } = fn(value.slice(a, b));
    const next = value.slice(0, a) + text + value.slice(b);
    setDraft({ ...draft, source: next });
    requestAnimationFrame(() => {
      el.focus();
      const [s, e] = select ?? [text.length, text.length];
      el.setSelectionRange(a + s, a + e);
    });
  };

  const bold = () => edit((sel) => (sel ? { text: `**${sel}**` } : { text: '**bold text**', select: [2, 11] }));
  const link = () => {
    const url = window.prompt('Link address (https://… or mailto:…)', 'https://');
    if (!url || !/^(https?:|mailto:)/i.test(url)) return;
    edit((sel) => ({ text: `[${sel || 'link text'}](${url})` }));
  };
  const insertName = () => edit(() => ({ text: '{{name}}' }));
  const insertAccount = () => edit(() => ({ text: `\n\n${ACCOUNT_DETAILS}\n\n` }));

  const run = async (kind: 'save' | 'test' | 'reset', fn: () => Promise<void>) => {
    setBusy(kind);
    try {
      await fn();
      if (showHistory) loadHistory();
    } catch (e: unknown) {
      toast({ title: 'That didn’t work', body: e instanceof Error ? e.message : undefined, icon: 'x' });
    } finally {
      setBusy(null);
    }
  };

  const save = () => run('save', async () => onSaved((await api.saveEmailTemplate(template.key, draft)).template, 'Email saved'));
  const test = () =>
    run('test', async () => {
      const r = await api.testEmailTemplate(template.key, { subject: draft.subject, source: draft.source });
      if (r.status === 'sent') toast({ title: 'Test sent', body: `Check ${r.to}`, icon: 'mail' });
      else if (r.status === 'not_configured') toast({ title: 'Email sending isn’t set up', body: 'Add the Resend key in Settings → Connections first.', icon: 'shield' });
      else toast({ title: 'The test wasn’t sent', body: r.status, icon: 'x' });
    });
  const reset = async () => {
    const ok = await ask({
      title: 'Use the built-in wording?',
      body: `“${template.name}” goes back to the wording JFH Forms came with. Your version stays in the history.`,
      confirmLabel: 'Use the built-in wording',
    });
    if (ok) void run('reset', async () => onSaved((await api.resetEmailTemplate(template.key)).template, 'Back to the built-in wording'));
  };
  const restore = async (v: EmailTemplateVersion) => {
    if (
      dirty &&
      !(await ask({ title: 'Replace your changes?', body: 'Restoring replaces the changes you haven’t saved.', confirmLabel: 'Restore', danger: true }))
    )
      return;
    void run('save', async () => onSaved((await api.restoreEmailTemplate(template.key, v.id)).template, 'Earlier version restored'));
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 14, minWidth: 0, alignItems: 'start' }}>
      {confirmDialog}
      <div className="card" style={{ padding: 'var(--pad-card)', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong style={{ fontSize: 15 }}>{template.name}</strong>
            <div style={{ marginTop: 3, fontSize: 13, color: 'var(--text-2)' }}>
              {notSent ? 'JFH Forms doesn’t send this email yet.' : sentWhen(template.when)}
            </div>
            {template.edited && template.updatedBy && template.updatedAt && (
              <div className="field-help" style={{ marginTop: 3 }}>
                Last changed by {template.updatedBy}, {fmt(template.updatedAt)}
              </div>
            )}
          </div>
          <div style={{ flex: 'none' }}>
            <Switch
              on={draft.enabled && !notSent}
              label={notSent ? 'Not sent yet' : draft.enabled ? 'Sent' : 'Switched off'}
              disabled={notSent}
              onChange={(on) => setDraft({ ...draft, enabled: on })}
            />
          </div>
        </div>

        <label className="field-label" htmlFor="email-subject" style={{ marginTop: 14 }}>
          Subject
        </label>
        <input
          id="email-subject"
          className="input"
          value={draft.subject}
          maxLength={200}
          onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
        />

        <label className="field-label" htmlFor="email-message" style={{ marginTop: 14 }}>
          Message
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          <button type="button" className="btn sm" onClick={bold} aria-label="Bold">
            <strong>B</strong> Bold
          </button>
          <button type="button" className="btn sm" onClick={link}>
            <Icon name="link" size={13} /> Link
          </button>
          <button type="button" className="btn sm" onClick={insertName}>
            <Icon name="user" size={13} /> Applicant’s name
          </button>
          <button type="button" className="btn sm" onClick={insertAccount} title="Only shown when an online account was created">
            <Icon name="shield" size={13} /> Online account details
          </button>
        </div>
        <textarea
          id="email-message"
          ref={area}
          className="textarea"
          rows={12}
          value={draft.source}
          onChange={(e) => setDraft({ ...draft, source: e.target.value })}
          style={{ fontSize: 13.5, lineHeight: 1.55, resize: 'vertical' }}
        />
        <div className="field-help" style={{ marginTop: 5 }}>
          Leave a blank line between paragraphs. Use the buttons above for bold, links and the applicant’s name.
          Online account details only appear when an online account was created.
        </div>
        {problem && (
          <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--st-declined)' }} role="alert">
            {problem}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginTop: 14 }}>
          <button className="btn primary" onClick={() => void save()} disabled={!dirty || !!problem || busy !== null}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" onClick={() => void test()} disabled={!!problem || busy !== null}>
            <Icon name="send" size={14} /> {busy === 'test' ? 'Sending…' : 'Send me a test'}
          </button>
          <button className="btn" onClick={() => void reset()} disabled={!template.edited || busy !== null}>
            {busy === 'reset' ? 'Changing…' : 'Use the built-in wording'}
          </button>
          <button className="btn" onClick={() => setShowHistory((s) => !s)} aria-expanded={showHistory} style={{ marginLeft: 'auto' }}>
            <Icon name="clock" size={14} /> History
          </button>
        </div>

        {showHistory && (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            {history === null ? (
              <div className="field-help">Loading…</div>
            ) : history.length === 0 ? (
              <div className="field-help">No saved versions yet. This email uses the built-in wording.</div>
            ) : (
              history.map((v) => (
                <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid var(--border)', fontSize: 13 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.subject}</div>
                    <div className="field-help">
                      {{ saved: 'Saved', reset: 'Built-in wording', restored: 'Restored' }[v.note]} by {v.savedBy}, {fmt(v.savedAt)}
                      {v.enabled ? '' : ' · switched off'}
                    </div>
                  </div>
                  <button className="btn sm" onClick={() => void restore(v)} disabled={busy !== null}>
                    Restore
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', minWidth: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-2)' }}>
          Preview with sample details · <strong>{preview?.subject ?? '…'}</strong>
          {(notSent || !draft.enabled) && (
            <span style={{ color: 'var(--st-declined)', fontWeight: 600 }}> · {notSent ? 'not sent yet' : 'switched off, won’t be sent'}</span>
          )}
        </div>
        <iframe
          title={`Preview of ${template.name}`}
          sandbox=""
          srcDoc={preview?.html ?? ''}
          style={{ width: '100%', height: 560, border: 'none', background: '#f4f6f5', display: 'block' }}
        />
      </div>
    </div>
  );
}
